import React, { useEffect, useRef, useState } from "react";
import { Loader2, ArrowDown } from "lucide-react";

/**
 * Pull-to-refresh on touch devices (esp. the Capacitor Android app).
 *
 * Why custom?  `react-simple-pull-to-refresh` ships its own scroller and
 * breaks our existing fixed header + nested scroll containers.  This component
 * just hooks into the page's natural window scroll: when the user starts
 * pulling DOWN while `window.scrollY === 0`, we show an indicator and fire
 * `onRefresh` once the threshold is crossed.
 *
 * Touch-only (mouse pointers skip it) so desktop UX stays untouched.
 *
 * Usage:
 *     <PullToRefresh onRefresh={() => loadFeed(true)}>
 *         <YourPage />
 *     </PullToRefresh>
 */
const THRESHOLD = 55;     // px of pull required to trigger a refresh
const MAX_PULL = 90;      // visual cap so the indicator doesn't fly off

export default function PullToRefresh({ onRefresh, children, disabled = false }) {
    const startY = useRef(null);
    const [pull, setPull] = useState(0);
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        if (disabled) return undefined;

        const onTouchStart = (e) => {
            if (window.scrollY > 0 || refreshing) return;
            startY.current = e.touches[0].clientY;
        };

        const onTouchMove = (e) => {
            if (startY.current == null || refreshing) return;
            const dy = e.touches[0].clientY - startY.current;
            if (dy <= 0) {
                setPull(0);
                return;
            }
            // Page already scrolled down — cancel.
            if (window.scrollY > 0) {
                startY.current = null;
                setPull(0);
                return;
            }
            // Rubber-band feel: progress slows as we approach MAX_PULL.
            const eased = Math.min(MAX_PULL, dy * 0.6);
            setPull(eased);
        };

        const onTouchEnd = async () => {
            if (refreshing) return;
            const reached = pull >= THRESHOLD;
            startY.current = null;
            setPull(0);
            if (!reached || typeof onRefresh !== "function") return;
            setRefreshing(true);
            try {
                await onRefresh();
            } finally {
                setRefreshing(false);
            }
        };

        document.addEventListener("touchstart", onTouchStart, { passive: true });
        document.addEventListener("touchmove", onTouchMove, { passive: true });
        document.addEventListener("touchend", onTouchEnd, { passive: true });
        document.addEventListener("touchcancel", onTouchEnd, { passive: true });
        return () => {
            document.removeEventListener("touchstart", onTouchStart);
            document.removeEventListener("touchmove", onTouchMove);
            document.removeEventListener("touchend", onTouchEnd);
            document.removeEventListener("touchcancel", onTouchEnd);
        };
    }, [pull, refreshing, onRefresh, disabled]);

    const visible = pull > 0 || refreshing;
    const ready = pull >= THRESHOLD;

    return (
        <>
            {visible && (
                <div
                    data-testid="pull-to-refresh-indicator"
                    aria-live="polite"
                    style={{
                        transform: `translateX(-50%) translateY(${Math.min(pull, MAX_PULL) - 50}px)`,
                        opacity: refreshing ? 1 : Math.min(1, pull / THRESHOLD),
                    }}
                    className="fixed top-3 left-1/2 z-[60] pointer-events-none flex flex-col items-center gap-1.5 text-aduku-navy"
                >
                    <div className="w-11 h-11 rounded-full bg-aduku-yellow shadow-lg flex items-center justify-center">
                        {refreshing ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                            <ArrowDown
                                className="w-5 h-5 transition-transform"
                                style={{ transform: ready ? "rotate(180deg)" : "rotate(0deg)" }}
                            />
                        )}
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-white/95 px-2 py-0.5 rounded-full">
                        {refreshing ? "Refreshing…" : ready ? "Release to refresh" : "Pull to refresh"}
                    </span>
                </div>
            )}
            <div
                style={{
                    transform: `translateY(${refreshing ? Math.min(THRESHOLD, MAX_PULL) : pull}px)`,
                    transition: pull === 0 ? "transform 180ms cubic-bezier(.22,.61,.36,1)" : "none",
                    // v78.25 — Only hint the compositor while the user is
                    // actively pulling / refreshing. Leaving `will-change:
                    // transform` permanently on this wrapper pins the
                    // entire product grid to its own layer, which
                    // Safari treats as "always dirty" — the whole grid
                    // re-rasterises on every scroll frame and flickers.
                    willChange: (pull > 0 || refreshing) ? "transform" : "auto",
                }}
            >
                {children}
            </div>
        </>
    );
}
